import type { StencilWizardPlugin, WizardContext } from '@stencil/cli';
import { access, mkdir, readFile, writeFile } from 'node:fs/promises';
import { join, relative, dirname } from 'node:path';

// ---------------------------------------------------------------------------
// Wrapper package scaffolding
// ---------------------------------------------------------------------------

/**
 * The core package's real npm name. This is not always its lowercased namespace
 * (`testy-test` vs `testytest`), and it's what the wrapper has to depend on and import from.
 */
async function readCorePackageName(rootDir: string, fallback: string): Promise<string> {
  try {
    const { name } = JSON.parse(await readFile(join(rootDir, 'package.json'), 'utf8'));
    return typeof name === 'string' && name ? name : fallback;
  } catch {
    return fallback;
  }
}

async function pathExists(p: string): Promise<boolean> {
  try {
    await access(p);
    return true;
  } catch {
    return false;
  }
}

async function scaffoldWrapperPackage(
  wrapperDir: string,
  packageName: string,
  corePackageName: string,
  corePkgVersion: string
): Promise<void> {
  await mkdir(join(wrapperDir, 'src'), { recursive: true });

  const pkgJson = {
    name: packageName,
    version: '0.0.1',
    type: 'module',
    main: './dist/index.js',
    types: './dist/index.d.ts',
    exports: { '.': { types: './dist/index.d.ts', import: './dist/index.js' } },
    scripts: { build: 'tsc', dev: 'tsc --watch' },
    peerDependencies: {
      react: '^18 || ^19',
      'react-dom': '^18 || ^19',
    },
    dependencies: { [corePackageName]: corePkgVersion },
    devDependencies: { typescript: '>=4 <7' },
  };

  const tsConfig = {
    compilerOptions: {
      target: 'ES2022',
      module: 'esnext',
      moduleResolution: 'bundler',
      declaration: true,
      rootDir: 'src',
      outDir: 'dist',
      strict: true,
      skipLibCheck: true,
    },
    include: ['src/**/*.ts', 'src/**/*.tsx'],
  };

  // components.ts lives at src/components.ts — generated by stencil build
  const indexTs =
    `// Re-exports React wrappers generated by @stencil/react-output-target.\n` +
    `// src/components.ts is populated by \`stencil build\` — do not edit manually.\n` +
    `export * from './components.js';\n`;

  await Promise.all([
    writeFile(join(wrapperDir, '.gitignore'), 'dist/\nnode_modules/\n', 'utf8'),
    writeFile(join(wrapperDir, 'package.json'), JSON.stringify(pkgJson, null, 2) + '\n', 'utf8'),
    writeFile(join(wrapperDir, 'tsconfig.json'), JSON.stringify(tsConfig, null, 2) + '\n', 'utf8'),
    writeFile(join(wrapperDir, 'src', 'index.ts'), indexTs, 'utf8'),
  ]);
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

export const wizard = {
  init: {
    id: '@stencil/react-output-target',
    displayName: 'React',
    description: 'Type-safe React wrappers for your Stencil components',

    async run({ config, workspaceRoot, prompts, nypm, openStencilConfig }: WizardContext): Promise<void> {
      const { intro, outro, text, confirm, spinner, isCancel, cancel, log } = prompts;

      intro('React output target');

      // Guard: already configured?
      const editor = await openStencilConfig();
      const alreadyConfigured = editor.outputTargetsContains('reactOutputTarget(');

      if (alreadyConfigured) {
        const redo = await confirm({
          message: 'reactOutputTarget is already in stencil.config.ts — reconfigure?',
          initialValue: false,
        });
        if (isCancel(redo) || !redo) {
          cancel('Skipping React setup.');
          return;
        }
      }

      // Where should the wrapper package live?
      let wrapperDir: string;
      const corePackageName = await readCorePackageName(config.rootDir, config.fsNamespace);
      let wrapperPackageName = `${config.fsNamespace}-react`;

      if (workspaceRoot) {
        const rawName = await text({
          message: 'Wrapper package name?',
          placeholder: wrapperPackageName,
          defaultValue: wrapperPackageName,
        });
        if (isCancel(rawName)) {
          cancel('Setup cancelled.');
          return;
        }
        wrapperPackageName = rawName as string;
        // Directory is already namespaced by living under packages/, so keep it
        // short rather than repeating the fsNamespace prefix from the package name.
        wrapperDir = join(dirname(config.rootDir), 'react');
      } else {
        const defaultRel = `../${config.fsNamespace}-react`;
        const rawDir = await text({
          message: 'Wrapper package directory? (relative to stencil.config.ts)',
          placeholder: defaultRel,
          defaultValue: defaultRel,
        });
        if (isCancel(rawDir)) {
          cancel('Setup cancelled.');
          return;
        }
        wrapperDir = join(config.rootDir, rawDir as string);
      }

      // Derive the stencil.config-relative outDir from the wrapper location
      const outDir = relative(config.rootDir, join(wrapperDir, 'src')).replace(/\\/g, '/');

      // SSR
      const enableSsr = await confirm({ message: 'Enable server-side rendering (SSR)?', initialValue: false });
      if (isCancel(enableSsr)) {
        cancel('Setup cancelled.');
        return;
      }

      // When not in a known monorepo, ask for the npm package name separately if SSR needs it
      if (enableSsr && !workspaceRoot) {
        const rawPkgName = await text({
          message: 'React wrapper npm package name? (used as clientModule in the SSR config)',
          placeholder: wrapperPackageName,
          defaultValue: wrapperPackageName,
        });
        if (isCancel(rawPkgName)) {
          cancel('Setup cancelled.');
          return;
        }
        wrapperPackageName = rawPkgName as string;
      }

      // Scaffold wrapper package if the directory doesn't exist yet
      const shouldScaffold = !(await pathExists(wrapperDir));
      if (shouldScaffold) {
        const s = spinner();
        s.start(`Scaffolding wrapper package at ${relative(workspaceRoot ?? config.rootDir, wrapperDir)}`);
        try {
          // workspace:* only resolves under pnpm/yarn-berry/bun — npm and yarn classic reject
          // the protocol outright (even for devDependencies that are never installed), so only
          // use it when the detected package manager actually understands it.
          const pm = workspaceRoot ? await nypm.detectPackageManager(workspaceRoot) : undefined;
          const supportsWorkspaceProtocol =
            pm?.name === 'pnpm' || pm?.name === 'bun' || (pm?.name === 'yarn' && pm.majorVersion !== '1');
          const corePkgVersion = supportsWorkspaceProtocol
            ? 'workspace:*'
            : `file:${relative(wrapperDir, config.rootDir).replace(/\\/g, '/')}`;
          await scaffoldWrapperPackage(wrapperDir, wrapperPackageName, corePackageName, corePkgVersion);
          s.stop('Wrapper package scaffolded');
        } catch (e) {
          s.stop('Scaffolding failed — continuing');
          log.warn(`Could not scaffold wrapper package: ${e}`);
        }
      }

      // Build the output target code. Multi-line snippets are indented relative to their own
      // start - the config editor re-indents them to whatever depth they get spliced in at.
      // With an `exports` map, only the `./ssr` entry is importable
      const ssrPath = config.generateExportMaps === true ? 'ssr' : 'dist/ssr';
      const targetCode = enableSsr
        ? `reactOutputTarget({\n  outDir: '${outDir}',\n  hydrateModule: '${corePackageName}/${ssrPath}',\n  clientModule: '${wrapperPackageName}',\n})`
        : `reactOutputTarget({ outDir: '${outDir}' })`;

      // Amend stencil.config.ts
      try {
        editor.addImport('@stencil/react-output-target', ['reactOutputTarget']);

        const hasStandalone =
          editor.outputTargetsContains("type: 'standalone'") ||
          editor.outputTargetsContains('type: "standalone"') ||
          editor.outputTargetsContains("type: 'dist-custom-elements'") ||
          editor.outputTargetsContains('type: "dist-custom-elements"');
        if (!hasStandalone) editor.addOutputTarget("{ type: 'standalone' }");

        if (enableSsr) {
          const hasSSR =
            editor.outputTargetsContains("type: 'ssr'") ||
            editor.outputTargetsContains('type: "ssr"') ||
            editor.outputTargetsContains("type: 'dist-hydrate-script'") ||
            editor.outputTargetsContains('type: "dist-hydrate-script"');
          if (!hasSSR) editor.addOutputTarget("{ type: 'ssr' }");
        }

        if (!editor.replaceOutputTarget('reactOutputTarget(', targetCode)) {
          editor.addOutputTarget(targetCode);
        }

        await editor.save();
        log.success('stencil.config.ts updated');
      } catch (e) {
        log.warn(
          `Could not automatically update stencil.config.ts (${e}). Add manually:\n\n` +
            `import { reactOutputTarget } from '@stencil/react-output-target';\n` +
            `// in outputTargets:\n${targetCode}` +
            (enableSsr ? `\n{ type: 'ssr' }` : '')
        );
      }

      // Install @stencil/react-output-target as a devDep of the core package
      log.info('Installing @stencil/react-output-target...');
      await nypm.addDependency(['@stencil/react-output-target'], { cwd: config.rootDir, dev: true });
      log.success('Installed @stencil/react-output-target');

      // Install React peer deps in the wrapper package
      if (shouldScaffold && (await pathExists(wrapperDir))) {
        log.info('Installing React peer dependencies in wrapper package...');
        // A regular dependency: the generated wrappers import its runtime (`/runtime`, `/ssr`).
        await nypm.addDependency(['@stencil/react-output-target'], { cwd: wrapperDir });
        await nypm.addDependency(['react', 'react-dom', '@types/react', '@types/react-dom', 'typescript'], {
          cwd: wrapperDir,
          dev: true,
        });
        log.success('React peer dependencies installed');
      }

      outro('React output target configured');
    },
  },
} satisfies StencilWizardPlugin;
